import type { Chart } from 'highcharts';
import { useCopyImageToClipboard } from 'hooks/useCopyToClipboard';
import { useCallback } from 'react';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';

export type Options = {
  chart?: Chart | null;
  canvas?: HTMLCanvasElement | null;
  width?: number;
};

export type ChartToImageOptions = Options & {
  onLoad: (canvas: HTMLCanvasElement, svg: string, img: HTMLImageElement) => void;
};

function chartToImage({ chart, canvas, width = 1000, onLoad }: ChartToImageOptions) {
  if (!chart || !canvas) {
    return;
  }
  const render_width = width;
  const render_height = (render_width * chart.chartHeight) / chart.chartWidth;

  // Get the chart's SVG code
  const svg = chart.exporting.getSVG({
    exporting: {
      sourceWidth: chart.chartWidth,
      sourceHeight: chart.chartHeight,
    },
  }) as string;
  canvas.height = render_height;
  canvas.width = render_width;
  // Create an image and draw the SVG onto the canvas
  const image = new Image();
  image.onload = function () {
    canvas.getContext('2d')!.drawImage(image, 0, 0, render_width, render_height);
    onLoad(canvas, svg, image);
  };

  image.src = 'data:image/svg+xml;base64,' + btoa(svg.replace(/[\u0250-\ue007]/g, ''));
}

export function useCopyChartToClipboard({ chart, canvas, width = 1000 }: Options) {
  const copyImgToClipboard = useCopyImageToClipboard();
  return useCallback(() => {
    chartToImage({
      chart,
      canvas,
      width,
      onLoad: canvas =>
        canvas.toBlob(blob => {
          copyImgToClipboard(blob);
        }),
    });
  }, [chart, canvas, width, copyImgToClipboard]);
}
